/* global BigInt */
// Structural UI copies only. Server SHA, ADMIN, stock and business admission
// remain server authority; none of these helpers creates that authority.
const MAX = 2147483647;
const MAX_LEDGER = '9007199254740991';
const READ = 'confirmed-preorder-incoming-admin-read-v1';
const REGISTRY = 'confirmed-preorder-command-registry-v2';
const TRANSPORT = 'confirmed-preorder-incoming-admin-transport-v2';
const PENDING = 'confirmed-preorder-incoming-admin-pending-v1';
const STORAGE_KEY = 'yw-preorder-incoming-pending-v1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SHA = /^[0-9a-f]{64}$/;
const VERSIONS = Object.freeze({initial: 'confirmed-preorder-incoming-v1',
  preparation: 'confirmed-preorder-incoming-preparation-v1', revision: 'confirmed-preorder-incoming-revision-v1'});
const COMMAND_KEYS = ['idempotencyKey', 'productId', 'childProductId', 'colorId', 'sizeId',
  'expectedPolicyRevision', 'confirmationRef', 'capacity', 'dispatchEta', 'payNowAllowed', 'payLaterAllowed'];
const REGISTRY_KEYS = ['schemaVersion', 'historicalOnly', 'confirmationProven', 'publicCheckoutAllowed'];
const ORIGINAL_KEYS = [...REGISTRY_KEYS, 'registryState', 'operation', 'operationId', 'commandVersion', 'commandSha256', 'createdAt', 'command'];
const CURSOR_KEYS = ['schemaVersion', 'ownerUserId', 'childProductId', 'sizeId', 'createdAt', 'operationId'];
const HANDLE_KEYS = ['schemaVersion', 'actorId', 'operationId', 'commandSha256'];
const RECEIPT_KEYS = ['schemaVersion', 'outcome', 'historicalOnly', 'publicCheckoutAllowed', 'requestId', 'batchId',
  'policyId', 'productId', 'childProductId', 'colorId', 'sizeId', 'confirmationRef', 'confirmedBy', 'confirmedAt',
  'capacity', 'dispatchEta', 'payNowAllowed', 'payLaterAllowed', 'policyRevision', 'ledgerRevision', 'confirmationSource', 'externallyVerified'];
const MESSAGES = Object.freeze({400: 'Invalid private incoming transport input', 401: 'Private incoming authentication required',
  403: 'Private incoming access denied', 404: 'Private incoming route not found', 405: 'Private incoming method not allowed',
  409: 'Private incoming conflict', 413: 'Private incoming body too large', 415: 'Private incoming media unsupported',
  429: 'Private incoming limit reached', 503: 'Private incoming transport unavailable'});
const boundedError = (kind = 'input', status = null, outcomeUnknown = false, recoveryRequired = false) =>
  Object.assign(new Error('Не удалось проверить операцию поставки.'), {kind, status, outcomeUnknown, recoveryRequired});
const requireValue = condition => {if (!condition) throw boundedError();};
const integer = value => Number.isSafeInteger(value) && value > 0 && value <= MAX;
const counter = value => Number.isSafeInteger(value) && value >= 0 && value <= MAX;
const bool = value => typeof value === 'boolean';
const uuid = value => typeof value === 'string' && UUID.test(value);
const sha = value => typeof value === 'string' && SHA.test(value);
const nullableId = value => value === null || integer(value);
const ledger = value => typeof value === 'string' && /^[1-9]\d{0,15}$/.test(value) &&
  (value.length < MAX_LEDGER.length || value.length === MAX_LEDGER.length && value <= MAX_LEDGER);
const record = (value, keys) => {
  requireValue(value && typeof value === 'object' && !Array.isArray(value) &&
    [Object.prototype, null].includes(Object.getPrototypeOf(value)) && Reflect.ownKeys(value).length === keys.length);
  const copy = {};
  for (const key of keys) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    requireValue(descriptor && descriptor.enumerable && Object.prototype.hasOwnProperty.call(descriptor, 'value'));
    copy[key] = descriptor.value;
  }
  return copy;
};
const tag = (value, key) => {
  requireValue(value && typeof value === 'object');
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  requireValue(descriptor && descriptor.enumerable && Object.prototype.hasOwnProperty.call(descriptor, 'value'));
  return descriptor.value;
};
const freeze = value => {
  if (value && typeof value === 'object') {Object.values(value).forEach(freeze); Object.freeze(value);}
  return value;
};
// Manual UTF-8 count is browser-safe and refuses lossy surrogate replacement.
const byteLength = text => {
  requireValue(typeof text === 'string' && !text.includes('\u0000'));
  let bytes = 0;
  for (let index = 0; index < text.length; index++) {
    const code = text.charCodeAt(index);
    if (code < 0x80) bytes++;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(++index);
      requireValue(next >= 0xdc00 && next <= 0xdfff); bytes += 4;
    } else {requireValue(code < 0xdc00 || code > 0xdfff); bytes += 3;}
  }
  return bytes;
};
const reference = value => typeof value === 'string' && /\S/.test(value) && byteLength(value) <= 128;
const sizeName = value => typeof value === 'string' && value.length <= 128 && /\S/.test(value) && byteLength(value) <= 512;
const exactTime = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value) &&
  Number.isSafeInteger(Date.parse(value)) && Date.parse(value) >= 0 && new Date(Date.parse(value)).toISOString() === value;
const etaCopy = raw => {
  const eta = record(raw, ['from', 'to']);
  requireValue(exactTime(eta.from) && exactTime(eta.to) && eta.from <= eta.to);
  return freeze(eta);
};
const versionFor = operation => {
  requireValue(typeof operation === 'string' && Object.prototype.hasOwnProperty.call(VERSIONS, operation));
  return VERSIONS[operation];
};
const normalize = (operation, raw) => {
  versionFor(operation);
  const command = record(raw, operation === 'revision' ? [...COMMAND_KEYS, 'batchId', 'expectedLedgerRevision'] : COMMAND_KEYS);
  requireValue(uuid(command.idempotencyKey) && ['productId', 'childProductId', 'colorId', 'sizeId', 'capacity'].every(key => integer(command[key])) &&
    (integer(command.expectedPolicyRevision) || operation === 'preparation' && command.expectedPolicyRevision === null) &&
    reference(command.confirmationRef) && bool(command.payNowAllowed) && bool(command.payLaterAllowed) &&
    (command.payNowAllowed || command.payLaterAllowed));
  command.dispatchEta = etaCopy(command.dispatchEta);
  if (operation === 'revision') requireValue(uuid(command.batchId) && ledger(command.expectedLedgerRevision));
  requireValue(byteLength(JSON.stringify(command)) <= 16384);
  return freeze(command);
};
export const normalizeIncomingCommand = (operation, raw) => {
  try {return normalize(operation, raw);} catch (_) {throw boundedError();}
};
export const moscowCalendarToUtc = text => {
  try {
    requireValue(typeof text === 'string' && /^\d{4}-\d\d-\d\d \d\d:\d\d$/.test(text));
    const [year, month, day, hour, minute] = [text.slice(0, 4), text.slice(5, 7), text.slice(8, 10), text.slice(11, 13), text.slice(14, 16)].map(Number);
    requireValue(year >= 1970 && month >= 1 && month <= 12 && day >= 1 && hour < 24 && minute < 60);
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
    requireValue(date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day &&
      date.getUTCHours() === hour && date.getUTCMinutes() === minute);
    const iso = new Date(date.getTime() - 10800000).toISOString(); requireValue(exactTime(iso)); return iso;
  } catch (_) {throw boundedError();}
};
const cursorCopy = raw => {
  const cursor = record(raw, CURSOR_KEYS);
  requireValue(cursor.schemaVersion === REGISTRY && integer(cursor.ownerUserId) && nullableId(cursor.childProductId) &&
    nullableId(cursor.sizeId) && exactTime(cursor.createdAt) && uuid(cursor.operationId));
  return freeze(cursor);
};
const bodyCopy = (operation, raw) => {
  let body;
  if (operation === 'target' || operation === 'current') {
    body = record(raw, operation === 'target' ? ['childProductId'] : ['childProductId', 'sizeId']);
    requireValue(Object.values(body).every(integer));
  } else if (operation === 'prepare') {
    body = record(raw, ['operation', 'command']); body.command = normalize(body.operation, body.command);
  } else if (operation === 'commands') {
    body = record(raw, ['childProductId', 'sizeId', 'before', 'limit']);
    requireValue(nullableId(body.childProductId) && nullableId(body.sizeId) && integer(body.limit) && body.limit <= 100);
    if (body.before !== null) {
      body.before = cursorCopy(body.before);
      requireValue(body.before.childProductId === body.childProductId && body.before.sizeId === body.sizeId);
    }
  } else {
    requireValue(['command', 'confirm', 'revise', 'recover'].includes(operation));
    body = record(raw, ['confirm', 'revise'].includes(operation) ? ['operationId', 'commandSha256', 'confirm'] : ['operationId', 'commandSha256']);
    requireValue(uuid(body.operationId) && (sha(body.commandSha256) || operation === 'command' && body.commandSha256 === null));
    if (operation === 'confirm' || operation === 'revise') requireValue(body.confirm === true);
  }
  return freeze(body);
};
export const requestBody = (operation, raw) => {
  try {return bodyCopy(operation, raw);} catch (_) {throw boundedError();}
};
const dense = (raw, cap, read) => {
  requireValue(Array.isArray(raw) && raw.length <= cap && Reflect.ownKeys(raw).length === raw.length + 1);
  const result = [];
  for (let index = 0; index < raw.length; index++) {
    const descriptor = Object.getOwnPropertyDescriptor(raw, String(index));
    requireValue(descriptor && descriptor.enumerable && Object.prototype.hasOwnProperty.call(descriptor, 'value'));
    result.push(read(descriptor.value));
  }
  return freeze(result);
};
const registryCommon = value => requireValue(value.schemaVersion === REGISTRY && value.historicalOnly === true &&
  value.confirmationProven === false && value.publicCheckoutAllowed === false);
const originalCopy = (raw, state) => {
  const copy = record(raw, ORIGINAL_KEYS); registryCommon(copy);
  requireValue(copy.registryState === state && copy.commandVersion === versionFor(copy.operation) && uuid(copy.operationId) &&
    sha(copy.commandSha256) && exactTime(copy.createdAt));
  copy.command = normalize(copy.operation, copy.command);
  requireValue(copy.command.idempotencyKey === copy.operationId);
  return freeze(copy);
};
// Saved business context and handle promotion may use either genuine original
// ACK, without rewriting it. Network prepare/command decoders stay distinct.
const originalAckCopy = raw => {
  const state = tag(raw, 'registryState');
  requireValue(state === 'prepared' || state === 'found');
  return originalCopy(raw, state);
};
const cursorBinding = (cursor, actor, body) => requireValue(cursor.ownerUserId === actor &&
  cursor.childProductId === body.childProductId && cursor.sizeId === body.sizeId);
const older = (left, right) => left.createdAt < right.createdAt ||
  left.createdAt === right.createdAt && left.operationId < right.operationId;
const pageCopy = (raw, actor, body) => {
  const page = record(raw, [...REGISTRY_KEYS, 'registryState', 'rows', 'nextCursor']); registryCommon(page);
  requireValue(page.registryState === 'page');
  const ids = new Set();
  page.rows = dense(page.rows, body.limit, rawRow => {
    const row = record(rawRow, ['operation', 'operationId', 'commandVersion', 'commandSha256', 'createdAt', 'childProductId', 'sizeId']);
    requireValue(row.commandVersion === versionFor(row.operation) && uuid(row.operationId) && sha(row.commandSha256) &&
      exactTime(row.createdAt) && integer(row.childProductId) && integer(row.sizeId) && !ids.has(row.operationId) &&
      (body.childProductId === null || body.childProductId === row.childProductId) && (body.sizeId === null || body.sizeId === row.sizeId));
    ids.add(row.operationId); return row;
  });
  if (body.before !== null) cursorBinding(body.before, actor, body);
  page.rows.forEach((row, index) => requireValue((index === 0 || older(row, page.rows[index - 1])) &&
    (body.before === null || older(row, body.before))));
  if (page.nextCursor !== null) {
    page.nextCursor = cursorCopy(page.nextCursor); cursorBinding(page.nextCursor, actor, body);
    const last = page.rows[page.rows.length - 1];
    requireValue(last && page.rows.length === body.limit && page.nextCursor.createdAt === last.createdAt && page.nextCursor.operationId === last.operationId);
  }
  return freeze(page);
};
const diagnosticsCopy = (operation, raw, body) => {
  if (operation === 'target') {
    const value = record(raw, ['schemaVersion', 'changed', 'readState', 'childProductId', 'candidates', 'publicCheckoutAllowed']);
    requireValue(value.schemaVersion === READ && value.changed === false && value.readState === 'candidates' &&
      value.childProductId === body.childProductId && value.publicCheckoutAllowed === false);
    let last = 0;
    value.candidates = dense(value.candidates, 100, rawCandidate => {
      const candidate = record(rawCandidate, ['sizeId', 'sizeName']);
      requireValue(integer(candidate.sizeId) && candidate.sizeId > last && sizeName(candidate.sizeName));
      last = candidate.sizeId; return candidate;
    });
    return freeze(value);
  }
  const value = record(raw, ['schemaVersion', 'changed', 'readState', 'identity', 'policy', 'batch', 'publicCheckoutAllowed']);
  requireValue(value.schemaVersion === READ && value.changed === false && value.publicCheckoutAllowed === false &&
    ['policy_absent', 'policy_unlinked', 'batch_linked'].includes(value.readState));
  value.identity = record(value.identity, ['productId', 'childProductId', 'colorId', 'sizeId', 'sizeName']);
  requireValue(['productId', 'childProductId', 'colorId', 'sizeId'].every(key => integer(value.identity[key])) &&
    value.identity.childProductId === body.childProductId && value.identity.sizeId === body.sizeId && sizeName(value.identity.sizeName));
  if (value.readState === 'policy_absent') requireValue(value.policy === null && value.batch === null);
  else {
    value.policy = record(value.policy, ['policyId', 'policyRevision', 'normalSalesEnabled', 'preorderEnabled']);
    requireValue(integer(value.policy.policyId) && integer(value.policy.policyRevision) && bool(value.policy.normalSalesEnabled) && bool(value.policy.preorderEnabled));
    if (value.readState === 'policy_unlinked') requireValue(value.batch === null);
    else {
      value.batch = record(value.batch, ['batchId', 'state', 'ledgerRevision', 'capacity', 'accountedQuantity', 'dispatchEta',
        'payNowAllowed', 'payLaterAllowed', 'initialConfirmedAt', 'currentConfirmedAt']);
      const batch = value.batch;
      requireValue(uuid(batch.batchId) && ['accepting', 'closed'].includes(batch.state) && ledger(batch.ledgerRevision) && integer(batch.capacity) &&
        counter(batch.accountedQuantity) && batch.accountedQuantity <= batch.capacity && bool(batch.payNowAllowed) && bool(batch.payLaterAllowed) &&
        (batch.payNowAllowed || batch.payLaterAllowed) && exactTime(batch.initialConfirmedAt) && exactTime(batch.currentConfirmedAt) &&
        batch.currentConfirmedAt >= batch.initialConfirmedAt && (batch.state !== 'closed' || value.policy.preorderEnabled === false));
      batch.dispatchEta = etaCopy(batch.dispatchEta);
    }
  }
  return freeze(value);
};
const savedCopy = (raw, body, operation) => {
  const saved = originalAckCopy(raw);
  requireValue(saved.operationId === body.operationId && saved.commandSha256 === body.commandSha256 &&
    (operation !== 'revise' || saved.operation === 'revision') && (operation !== 'confirm' || saved.operation !== 'revision'));
  return saved;
};
const businessCopy = (operation, raw, actor, saved) => {
  if (operation === 'recover' && raw && typeof raw === 'object' && Object.getOwnPropertyDescriptor(raw, 'recoveryState')) {
    const result = record(raw, ['schemaVersion', 'recoveryState', 'historicalOnly', 'publicCheckoutAllowed', 'retryWithSameKey']);
    requireValue(result.schemaVersion === saved.commandVersion && result.recoveryState === 'not_found' &&
      result.historicalOnly === true && result.publicCheckoutAllowed === false && result.retryWithSameKey === true);
    return freeze(result);
  }
  const result = record(raw, ['outcome', 'receipt']); requireValue(result.outcome === 'confirmed');
  const receipt = record(result.receipt, saved.operation === 'revision' ? [...RECEIPT_KEYS, 'capacityDelta'] : RECEIPT_KEYS);
  const command = saved.command;
  requireValue(receipt.schemaVersion === saved.commandVersion && receipt.outcome === 'confirmed' && receipt.historicalOnly === true &&
    receipt.publicCheckoutAllowed === false && uuid(receipt.requestId) && uuid(receipt.batchId) && integer(receipt.policyId) &&
    receipt.confirmedBy === 'admin:' + actor && exactTime(receipt.confirmedAt) && reference(receipt.confirmationRef) &&
    receipt.confirmationSource === 'responsible_admin_declaration' && receipt.externallyVerified === false && integer(receipt.policyRevision) && ledger(receipt.ledgerRevision));
  for (const key of ['productId', 'childProductId', 'colorId', 'sizeId', 'confirmationRef', 'capacity', 'payNowAllowed', 'payLaterAllowed'])
    requireValue(receipt[key] === command[key]);
  receipt.dispatchEta = etaCopy(receipt.dispatchEta);
  requireValue(JSON.stringify(receipt.dispatchEta) === JSON.stringify(command.dispatchEta) &&
    receipt.policyRevision === (command.expectedPolicyRevision === null ? 1 : command.expectedPolicyRevision + 1));
  if (saved.operation === 'revision') {
    requireValue(receipt.batchId === command.batchId && receipt.ledgerRevision === (BigInt(command.expectedLedgerRevision) + 1n).toString() &&
      Number.isSafeInteger(receipt.capacityDelta) && Math.abs(receipt.capacityDelta) <= MAX && integer(receipt.capacity - receipt.capacityDelta));
  } else requireValue(receipt.ledgerRevision === '1');
  result.receipt = receipt; return freeze(result);
};
export const readIncomingDto = (operation, raw, expected) => {
  try {
    const business = ['confirm', 'revise', 'recover'].includes(operation);
    const context = record(expected, business ? ['actorId', 'body', 'saved'] : ['actorId', 'body']);
    requireValue(integer(context.actorId)); const body = bodyCopy(operation, context.body);
    if (operation === 'target' || operation === 'current') return diagnosticsCopy(operation, raw, body);
    if (operation === 'commands') return pageCopy(raw, context.actorId, body);
    if (operation === 'prepare') {
      const result = originalCopy(raw, 'prepared');
      requireValue(result.operation === body.operation && JSON.stringify(result.command) === JSON.stringify(body.command)); return result;
    }
    if (operation === 'command') {
      if (tag(raw, 'registryState') === 'not_found') {
        const result = record(raw, [...REGISTRY_KEYS, 'registryState']); registryCommon(result); return freeze(result);
      }
      const result = originalCopy(raw, 'found');
      requireValue(result.operationId === body.operationId && (body.commandSha256 === null || result.commandSha256 === body.commandSha256)); return result;
    }
    return businessCopy(operation, raw, context.actorId, savedCopy(context.saved, body, operation));
  } catch (_) {const uncertain = !['target', 'current'].includes(operation); throw boundedError('contract', null, uncertain, uncertain);}
};
export const readIncomingError = (status, raw) => {
  try {
    const value = record(raw, ['schemaVersion', 'message', 'outcomeUnknown', 'recoveryRequired']);
    requireValue(Number.isInteger(status) && Object.prototype.hasOwnProperty.call(MESSAGES, status) && value.schemaVersion === TRANSPORT &&
      value.message === MESSAGES[status] && bool(value.outcomeUnknown) && bool(value.recoveryRequired));
    return boundedError('http', status, value.outcomeUnknown, value.recoveryRequired);
  } catch (_) {throw boundedError('contract');}
};
const handleCopy = raw => {
  const handle = record(raw, HANDLE_KEYS);
  requireValue(handle.schemaVersion === PENDING && integer(handle.actorId) && uuid(handle.operationId) &&
    (handle.commandSha256 === null || sha(handle.commandSha256))); return freeze(handle);
};
const sameHandle = (left, right) => HANDLE_KEYS.every(key => left[key] === right[key]);
export const inspectPending = (storage, actorId) => {
  requireValue(integer(actorId));
  let text;
  try {text = storage.getItem(STORAGE_KEY);} catch (_) {return freeze({state: 'unavailable'});}
  if (text === null) return freeze({state: 'none'});
  try {
    requireValue(typeof text === 'string' && byteLength(text) <= 1024);
    const pending = handleCopy(JSON.parse(text)); requireValue(JSON.stringify(pending) === text);
    return freeze(pending.actorId === actorId ? {state: 'owned-valid', pending} : {state: 'foreign'});
  } catch (_) {return freeze({state: 'corrupt'});}
};
export const savePending = (storage, raw) => {
  let handle;
  try {handle = handleCopy(raw); requireValue(handle.commandSha256 === null);} catch (_) {throw boundedError();}
  const current = inspectPending(storage, handle.actorId);
  if (current.state === 'owned-valid') return sameHandle(current.pending, handle);
  if (current.state !== 'none') return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(handle)); const after = inspectPending(storage, handle.actorId);
    return after.state === 'owned-valid' && sameHandle(after.pending, handle);
  } catch (_) {return false;}
};
export const promotePending = (storage, raw, preparedRaw) => {
  let handle, prepared;
  try {handle = handleCopy(raw); requireValue(handle.commandSha256 === null); prepared = originalAckCopy(preparedRaw);}
  catch (_) {return false;}
  if (prepared.operationId !== handle.operationId) return false;
  const promoted = freeze({...handle, commandSha256: prepared.commandSha256}), current = inspectPending(storage, handle.actorId);
  if (current.state !== 'owned-valid') return false;
  if (sameHandle(current.pending, promoted)) return true;
  if (!sameHandle(current.pending, handle)) return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(promoted)); const after = inspectPending(storage, handle.actorId);
    return after.state === 'owned-valid' && sameHandle(after.pending, promoted);
  } catch (_) {return false;}
};
export const clearPending = (storage, raw) => {
  let handle; try {handle = handleCopy(raw);} catch (_) {throw boundedError();}
  if (handle.commandSha256 === null) return false;
  const current = inspectPending(storage, handle.actorId);
  if (current.state !== 'owned-valid' || !sameHandle(current.pending, handle)) return false;
  try {storage.removeItem(STORAGE_KEY); return inspectPending(storage, handle.actorId).state === 'none';} catch (_) {return false;}
};
export const mergeIncomingUncertainty = (previous, error) => {
  try {
    const flags = record(previous, ['outcomeUnknown', 'recoveryRequired']);
    const unknown = tag(error, 'outcomeUnknown'), recovery = tag(error, 'recoveryRequired');
    requireValue(bool(flags.outcomeUnknown) && bool(flags.recoveryRequired) && bool(unknown) && bool(recovery));
    return freeze({outcomeUnknown: flags.outcomeUnknown || unknown, recoveryRequired: flags.recoveryRequired || recovery});
  } catch (_) {throw boundedError();}
};
